import * as _ from 'lodash';
import * as d3 from 'd3';
import * as d3a from 'd3-array';
import * as h3 from 'h3-js';
import * as geojson2h3 from 'geojson2h3';
import * as simple from 'simple-statistics';
import { MeasurementDataDTO } from 'src/app/web-api-client';

export abstract class HexagonData {
    protected _data: any[];
    protected _h3IndexCache = new Map();
    protected _lastCacheKey: number;

    get empty(): boolean {
        return this._data.length == 0;
    }

    constructor(data: any[]) {
        this._data = data;
    }

    /**
     * Converts the data to a map of hexId to sample hexagon data
     * @param data samples data
     * @param zoomLevel zoom level
     * @returns map of hexId to sample hexagon data
     */
    toHexagonDataMap(zoomLevel: number): Map<string, any> {
        const cacheKey = zoomLevel;

        if (!this._h3IndexCache.has(cacheKey)) {
            const hexIdxGrp = this.getHexagons(zoomLevel);
            this._h3IndexCache.set(cacheKey, hexIdxGrp);
        }

        this._lastCacheKey = cacheKey;
        return this.getCachedHexagonDataMap(cacheKey);
    }

    protected getCachedHexagonDataMap(key): Map<string, any> {
        return this._h3IndexCache.get(key);
    }

    protected getLastCachedHexagonDataMap(): Map<string, any> {
        return this.getCachedHexagonDataMap(this._lastCacheKey);
    }

    abstract getColorData(): any[];

    abstract getHexagons(zoomLevel: number): Map<string, any>;
}

export class SamplesHexagonData extends HexagonData {
    getColorData(): any[] {
        return [...this.getLastCachedHexagonDataMap().values()].map((d) => d.data);
    }

    getHexagons(zoomLevel: number): Map<string, any> {
        return d3.rollup(
            this._data,
            (v) => {
                return { data: v.length, statistics: { numberOfSamples: v.length } };
            },
            (d) => {
                return (d.h3Idx = h3.latLngToCell(d['latitude'], d['longitude'], zoomLevel));
            }
        );
    }
}

export class MeasurementsHexagonData extends HexagonData {
    private _aggregationMode: string;

    constructor(data: any[], aggregationMode: string) {
        super(data);
        this._aggregationMode = aggregationMode;
    }

    getColorData(): any[] {
        if (this._aggregationMode == 'maxActivity') {
            return [...this.getLastCachedHexagonDataMap().values()].map((d) => d.statistics.maxActivity).filter((d) => d !== 0);
        }

        if (this._aggregationMode == 'medianActivity') {
            return [...this.getLastCachedHexagonDataMap().values()].map((d) => d.statistics.medianActivity).filter((d) => d !== 0);
        }

        if (this._aggregationMode == 'meanActivity') {
            return [...this.getLastCachedHexagonDataMap().values()].map((d) => d.statistics.meanActivity).filter((d) => d !== 0);
        }

        if (this._aggregationMode == 'deviationActivity') {
            return [...this.getLastCachedHexagonDataMap().values()].map((d) => d.statistics.deviationActivity).filter((d) => d !== 0);
        }

        if (this._aggregationMode == 'geometricMean') {
            return [...this.getLastCachedHexagonDataMap().values()].map((d) => d.statistics.geometricMeanActivity).filter((d) => d !== 0);
        }

        if (this._aggregationMode == 'geometricStandardDeviation') {
            return [...this.getLastCachedHexagonDataMap().values()].map((d) => d.statistics.geometricStandardDeviationActivity).filter((d) => d !== 0);
        }

        return [];
    }

    getHexagons(zoomLevel: number): Map<string, any> {
        return d3.rollup(
            this._data.map((d) => ({
                ...d,
                h3Idx: h3.latLngToCell(d['latitude'], d['longitude'], zoomLevel),
            })),
            (v) => {
                let samplesWithEqualValueType = v.filter((d) => d.detection === '=');
                const isC14 = v.length && v[0].nuclideId === "3";
                if(!isC14) {
                    samplesWithEqualValueType = samplesWithEqualValueType.filter((d) => d.activity !== 0);
                }
                const numberOfSamples = new Set(v.map((d) => d.sampleId)).size;
                const numberOfSamplesWithEqualType = new Set(v.filter((d) => d.detection === '=').map((d) => d.sampleId)).size;
                const numberOfSamplesWithoutEqualType = new Set(v.filter((d) => d.detection !== '=').map((d) => d.sampleId)).size;
                // Aggregate statistics use activityCorr (the unit-normalized
                // value CalculateActivityCorr computes to match the unitCorr
                // label the legend displays), not the raw activity: samples in
                // the same hexagon can legitimately have been submitted in
                // different original units (e.g. Bq/kg vs Bq/m3 seawater, or
                // dry- vs wet-weight biota), and averaging/maxing/etc. their
                // raw magnitudes together produces statistics that don't match
                // what the legend claims to show. activityCorr is null for
                // unit/sample-type combinations CalculateActivityCorr doesn't
                // support, so it's filtered to finite values here, same as any
                // other input to these statistics. For C-14 (Δ14C, unit
                // DELTA/mill) activityCorr passes activity through unchanged,
                // so this has no effect on that nuclide's existing behavior.
                const activities = samplesWithEqualValueType.map((d) => d.activityCorr).filter((activityCorr: number) => Number.isFinite(activityCorr));
                const hasAnyActivity = activities.length != 0;
                const stdevV = calculatePopulationStdev(activities);
                const gstddevV = gstddev(activities);
                const gmeanV = gmean(activities);
                const meanV = d3.mean(activities);

                const statistics = {
                    maxActivity: hasAnyActivity ? d3.max(activities) : 0,
                    maxActivityString: !hasAnyActivity ? 'N/A' : d3smartFormat(d3.max(activities)),
                    medianActivity: hasAnyActivity ? d3a.median(activities) : 0,
                    medianActivityString: !hasAnyActivity ? 'N/A' : d3smartFormat(d3a.median(activities)),
                    meanActivity: hasAnyActivity ? meanV : 0,
                    meanActivityString: !hasAnyActivity ? 'N/A' : d3smartFormat(meanV),
                    numberOfSamples,
                    numberOfSamplesWithEqualType,
                    numberOfSamplesWithoutEqualType,
                    deviationActivity: hasAnyActivity ? stdevV : 0,
                    deviationActivityString: !hasAnyActivity ? 'N/A' : d3smartFormat(stdevV),
                    geometricMeanActivity: hasAnyActivity ? gmeanV : 0,
                    geometricMeanActivityString: !hasAnyActivity ? 'N/A' : d3smartFormat(gmeanV),
                    geometricStandardDeviationActivity: hasAnyActivity ? gstddevV : 0,
                    geometricStandardDeviationActivityString: !hasAnyActivity ? 'N/A' : d3smartFormat(gstddevV),
                    hasAnyActivity,
                    isStandardDeviationNA: isC14 || Number.isNaN(stdevV),
                    isGeometricStandardDeviationActivityNA: isC14 || Number.isNaN(gstddevV),
                    isMeanActivityNA: Number.isNaN(meanV),
                    isGeometricMeanActivityNA: Number.isNaN(gmeanV),
                    AMSD: hasAnyActivity ? `${d3smartFormat(meanV)} ± ${d3smartFormat(stdevV)}` : 'N/A',
                    GMGSD: hasAnyActivity && !isC14 ? `${d3smartFormat(gmeanV)} ± ${d3smartFormat(gstddevV)}` : 'N/A',
                    isC14
                };

                if (this._aggregationMode == 'maxActivity') {
                    return { data: statistics.maxActivity, length: v.length, statistics };
                }

                if (this._aggregationMode == 'medianActivity') {
                    return { data: statistics.medianActivity, statistics };
                }

                if (this._aggregationMode == 'meanActivity') {
                    return { data: statistics.meanActivity, statistics };
                }

                if (this._aggregationMode == 'deviationActivity') {
                    return { data: statistics.deviationActivity, statistics };
                }

                if (this._aggregationMode == 'geometricMean') {
                    return { data: statistics.geometricMeanActivity, statistics };
                }

                if (this._aggregationMode == 'geometricStandardDeviation') {
                    return { data: statistics.geometricStandardDeviationActivity, statistics };
                }
            },
            (d) => d.h3Idx
        );
    }
}

export const colorDomain = (data) => {
    const sortedData = data
        .slice()
        .filter((d) => d)
        .sort((a, b) => a - b);

    const quantiles = [0.25, 0.5, 0.75, 0.95, 0.99];
    const f = function (d) {
        const quantile = d3.quantile(sortedData, d);
        return Math.round(quantile);
    };

    return quantiles.map((d) => f(d));
};

export const bboxToPoly = (bbox) => {
    return [
        [bbox._sw.lng, bbox._ne.lat],
        [bbox._sw.lng, bbox._sw.lat],
        [bbox._ne.lng, bbox._sw.lat],
        [bbox._ne.lng, bbox._ne.lat],
    ];
};

export const zoomScale = d3.scaleThreshold().domain([3.5, 4.5, 6, 8, 9, 10, 12]).range([3, 4, 5, 6, 7, 8, 9, 10]);

const clusterCountFunction = d3.scaleThreshold([2, 5, 10], [0, 2, 4, 5]);

export interface LogCkmeansColorScale {
    // Ascending signed-log-space breakpoints (ckmeans cluster centroids;
    // see signedLog10 below). Values are clustered in log space -- rather
    // than raw magnitude -- because measurement activities legitimately
    // span many orders of magnitude (e.g. 1e-3 to 1e3); a linear scale
    // would make everything but the single largest value look like the
    // same color. The signed-log transform extends this to negative
    // values (e.g. Δ14C, which is legitimately signed) on the same
    // continuous axis.
    logBreaks: number[];
    // The same breakpoints converted back to linear-space values
    // (invSignedLog10(logBreaks)), for display (legend tick labels).
    breaks: number[];
    // One color per breakpoint, same length/order as breaks.
    colors: string[];
}

// Linear-region constant for signedLog10/invSignedLog10 below. Chosen far
// below the smallest realistic measurement magnitude in this dataset (the
// ~1e-3 lower bound documented above) so that for every real value ckmeans
// ever sees, signedLog10 is numerically indistinguishable from a plain
// sign(x) * log10(|x|) -- confirmed against MARIS_QA's real C-14 Δ14C
// distribution (11,102 rows) across four candidate thresholds (1e-3, 1e-6,
// 1e-9, 1e-12, 2026-09 audit): resulting ckmeans breaks were identical to
// 2 decimal places in every case. Its only real role is keeping the
// transform finite at x = 0.
const SIGNED_LOG_LINEAR_THRESHOLD = 1e-9;

// Generalizes Math.log10 to the full real line (including 0 and negative
// values) so ckmeans clustering and Mapbox `interpolate` can place
// positive and negative measurements on one continuous, monotonically
// increasing axis instead of requiring positive-only input. For
// |x| >> SIGNED_LOG_LINEAR_THRESHOLD (true of every realistic measurement
// in this dataset), signedLog10(x) equals sign(x) * log10(|x|) shifted by
// the constant -log10(SIGNED_LOG_LINEAR_THRESHOLD) -- i.e. *differences*
// between transformed values are preserved, which is what ckmeans cluster
// assignment and `interpolate` ratios actually depend on, so clustering
// and rendered colors for positive-only data are unaffected by this
// change (breaks, converted back via invSignedLog10, are unchanged).
export function signedLog10(x: number): number {
    return Math.sign(x) * Math.log10(1 + Math.abs(x) / SIGNED_LOG_LINEAR_THRESHOLD);
}

// Inverse of signedLog10, used to convert cluster-centroid breaks back to
// linear-space values for legend tick labels.
export function invSignedLog10(y: number): number {
    return Math.sign(y) * SIGNED_LOG_LINEAR_THRESHOLD * (10 ** Math.abs(y) - 1);
}

// Same anchor palette validated in the maris-skew-comparison log-ckmeans
// prototype (YlGnBu extended with a purple high-end anchor). Anchors are
// picked by eye for perceptual spacing; interpolation *between* them (both
// here, when more than 7 stops are requested, and at hexagon-paint time in
// colorForLogCkmeansValue/Mapbox) is plain RGB, so it matches Mapbox GL's
// native `interpolate` exactly.
const logColorAnchors = ['rgb(255, 255, 204)', 'rgb(194, 230, 153)', 'rgb(120, 198, 121)', 'rgb(65, 182, 196)', 'rgb(29, 145, 192)', 'rgb(37, 52, 148)', 'rgb(74, 20, 140)'];

// Picks `steps` colors from the anchor palette above. Our adaptive cluster
// count never exceeds the anchor count in practice (clusterCountFunction
// tops out at 6 clusters), so this is normally a plain slice; the
// interpolated fallback only matters if that heuristic ever changes.
function buildPerceptualColorStops(steps: number): string[] {
    if (steps <= 1) {
        return [logColorAnchors[0]];
    }

    if (steps <= logColorAnchors.length) {
        return logColorAnchors.slice(0, steps);
    }

    return d3.range(steps).map((index: number) => {
        const position = (index / (steps - 1)) * (logColorAnchors.length - 1);
        const lowerIndex = Math.floor(position);
        const upperIndex = Math.ceil(position);
        if (lowerIndex === upperIndex) {
            return logColorAnchors[lowerIndex];
        }
        return lerpRgb(logColorAnchors[lowerIndex], logColorAnchors[upperIndex], position - lowerIndex);
    });
}

// ckmeans cluster centroids can tie (or nearly tie) when a cluster contains
// a single repeated value. Nudge duplicates up by a tiny epsilon instead of
// collapsing them, so the scale always ends up with strictly increasing
// stops -- required by Mapbox's `interpolate` expression.
function ensureStrictlyIncreasing(values: number[]): number[] {
    return values.reduce((acc: number[], value: number, index: number) => {
        if (index === 0) {
            acc.push(value);
            return acc;
        }

        const previous = acc[acc.length - 1];
        acc.push(value > previous ? value : previous + Math.max(Math.abs(previous) * 1e-9, Number.EPSILON));
        return acc;
    }, []);
}

// Builds a continuous signed-log-scale color mapping for hexagon values:
// values are clustered via ckmeans in signed-log space (using the same
// adaptive distinct-value-count-driven cluster count as the sample-count
// display), and a color is assigned per cluster centroid. Positive and
// negative values are both valid input (see signedLog10 above); only
// non-finite values are excluded. Returns null when there is no usable
// data to color (e.g. before data loads, or an empty filter result) --
// callers should treat that as "nothing to render".
export const buildLogCkmeansColorScale = (values: number[]): LogCkmeansColorScale | null => {
    const usableValues = values.filter((v: number) => Number.isFinite(v));
    if (usableValues.length === 0) {
        return null;
    }

    const logValues = usableValues.map((v: number) => signedLog10(v));
    const distinctCount = new Set(usableValues).size as any;
    const clustersCount = clusterCountFunction(distinctCount);
    // Clamp to distinctCount (not just the raw value count): ckmeans cannot
    // usefully split fewer distinct values than requested clusters, and
    // asking it to would risk empty clusters / NaN centroids for datasets
    // with many repeated values.
    const nClusters = Math.max(1, Math.min(clustersCount + 1, distinctCount));

    const clusters = simple.ckmeans(logValues, nClusters);
    const logCentroids = clusters.map((cluster: number[]) => d3.mean(cluster));
    const strictLogBreaks = ensureStrictlyIncreasing(logCentroids);
    // A single cluster (e.g. all values identical) still needs 2 distinct
    // stops for a valid Mapbox `interpolate` expression; duplicate it with
    // the same color so the scale renders as a flat swatch instead of
    // failing to render.
    const logBreaks = strictLogBreaks.length > 1 ? strictLogBreaks : [strictLogBreaks[0], strictLogBreaks[0] + Math.max(Math.abs(strictLogBreaks[0]) * 1e-9, Number.EPSILON)];
    const colors = buildPerceptualColorStops(logBreaks.length);

    return {
        logBreaks,
        breaks: logBreaks.map((logValue) => invSignedLog10(logValue)),
        colors,
    };
};

// Linear interpolation between two CSS color strings in plain RGB space
// (not HCL). This intentionally matches Mapbox GL's native
// `interpolate`/`linear` expression, which blends colors channel-by-channel
// in RGB -- so the *discrete* anchor colors above are chosen with a nicer
// perceptual (HCL) spacing, but the interpolation *between* them uses the
// same plain RGB lerp on both the Mapbox (GPU) side and the
// PolarHexagonCanvasOverlay (JS/canvas) side. That keeps polar and
// non-polar hexagons pixel-identical instead of relying on two different
// interpolation engines to agree.
function lerpRgb(colorA: string, colorB: string, t: number): string {
    const a = d3.rgb(colorA);
    const b = d3.rgb(colorB);
    return `rgb(${Math.round(a.r + (b.r - a.r) * t)}, ${Math.round(a.g + (b.g - a.g) * t)}, ${Math.round(a.b + (b.b - a.b) * t)})`;
}

// Plain-JS mirror of the Mapbox expression built by
// `asMapboxLogCkmeansInterpolateExpr`, evaluated against the same scale.
// Used by PolarHexagonCanvasOverlay (which paints on a <canvas>, outside
// Mapbox's GPU expression pipeline) and by the legend gradient, so both
// renderers and the legend agree on colors for the same value.
export const colorForLogCkmeansValue = (value: number, scale: LogCkmeansColorScale | null): string => {
    if (!scale || !Number.isFinite(value)) {
        return 'transparent';
    }

    const logValue = signedLog10(value);
    const { logBreaks, colors } = scale;

    if (logValue <= logBreaks[0]) {
        return colors[0];
    }
    if (logValue >= logBreaks[logBreaks.length - 1]) {
        return colors[colors.length - 1];
    }

    for (let i = 0; i < logBreaks.length - 1; i++) {
        if (logValue >= logBreaks[i] && logValue <= logBreaks[i + 1]) {
            const t = (logValue - logBreaks[i]) / (logBreaks[i + 1] - logBreaks[i]);
            return lerpRgb(colors[i], colors[i + 1], t);
        }
    }

    return colors[colors.length - 1];
};

// Mapbox GL `interpolate`/`linear` stop list, keyed on signedLog10(value)
// (see asMapboxSignedLog10Expr below) so the GPU-evaluated color exactly
// matches `colorForLogCkmeansValue` above for the same scale. Wrapped in an
// outer `case` by buildLogCkmeansFillColorExpression below, which routes the
// no-data sentinel to transparent before this ever runs.
export const asMapboxLogCkmeansInterpolateExpr = (scale: LogCkmeansColorScale) => {
    return _.flatten(scale.logBreaks.map((logBreak, index) => [logBreak, scale.colors[index]]));
};

// GPU-evaluated mirror of signedLog10, expressed in Mapbox GL expression
// syntax. The Mapbox style spec has no native `sign` function, so it's
// built from `case`/`abs`/`log10`/arithmetic instead; this must stay
// mathematically identical to signedLog10 above so the Mapbox `fill-color`
// paint property (GPU) and PolarHexagonCanvasOverlay/legend (JS, via
// colorForLogCkmeansValue) agree on color for the same value. Takes the
// Mapbox expression that resolves to the raw value (e.g.
// ['get', 'dataValue']) and returns an expression computing signedLog10 of
// it, for use as the input key to an `interpolate` expression built from
// asMapboxLogCkmeansInterpolateExpr.
export const asMapboxSignedLog10Expr = (valueExpr: any) => [
    '*',
    ['case', ['<', valueExpr, 0], -1, ['>', valueExpr, 0], 1, 0],
    ['log10', ['+', 1, ['/', ['abs', valueExpr], SIGNED_LOG_LINEAR_THRESHOLD]]],
];

// Builds the Mapbox `fill-color` paint expression for hexbins-fill: the
// signed-log interpolate expression above, wrapped in a `case` that routes
// no-data hexagons to transparent. getHexagonsFeatureCollection's `dataValue`
// is a number for every hexagon -- `0` for a hexagon with no eligible
// activity (see MeasurementsHexagonData.getHexagons' hasAnyActivity), never
// null/undefined -- so the no-data check here must be `dataValue === 0`, not
// a null check (a `=== null` check here is always false and was a
// regression: it let no-data hexagons fall through to the interpolate branch
// and get colored at signedLog10(0), indistinguishable from a real low
// value). This mirrors PolarHexagonCanvasOverlay.drawFeature's own
// `value === 0` skip, so polar and non-polar hexagons treat no-data
// identically. Real zero/negative *measured* values are unaffected: only a
// hexagon with zero eligible samples produces `dataValue === 0` in the first
// place (see getColorData's `!== 0` domain filter and the `hasAnyActivity`
// ternaries in getHexagons), so this does not hide real data -- see
// colorForLogCkmeansValue's own handling of an actual value of 0, which
// remains colorable.
export const buildLogCkmeansFillColorExpression = (scale: LogCkmeansColorScale | null) => {
    if (!scale) {
        return 'transparent';
    }

    return ['case', ['==', ['get', 'dataValue'], 0], 'transparent', ['interpolate', ['linear'], asMapboxSignedLog10Expr(['get', 'dataValue']), ...asMapboxLogCkmeansInterpolateExpr(scale)]];
};

export interface GradientLegendTick {
    left: string; // CSS percentage, e.g. "37.5%"
    label: string;
}

export interface GradientLegend {
    gradientBackground: string; // CSS linear-gradient(...)
    ticks: GradientLegendTick[];
}

// Builds a CSS gradient + tick layout for a LogCkmeansColorScale.
//
// Ticks sit at evenly-spaced fixed slots (0%, 100/(n-1)%, ..., 100%) rather
// than at each breakpoint's true log-space position. ckmeans breaks
// legitimately cluster close together in log space for skewed data (e.g.
// p90/p99 nearly touching), which crowded/overlapped their number labels
// when positioned by true log position. This legend is a readable color
// *key* -- which anchor color a value is nearest to -- not a literal number
// line, so the gradient's color stops use the same fixed slots as the
// ticks: a hexagon colored via colorForLogCkmeansValue/the Mapbox expression
// still always falls on-or-between two of these anchor colors, in the same
// left-to-right order shown here.
// Relative tolerance for detecting the synthetic epsilon-padded gap
// buildLogCkmeansColorScale creates when every input value is identical
// (its `strictLogBreaks.length > 1 ? ... : ...` branch, which pads a
// single-cluster scale to the two logBreaks/colors Mapbox's `interpolate`
// requires). Deliberately three orders of magnitude looser than the 1e-9
// relative gap that padding creates, to absorb floating-point rounding in
// the add-then-subtract round trip (logBreaks[0] + gap, then
// logBreaks[1] - logBreaks[0] to recover it loses precision once gap is
// ~1e-9 relative to logBreaks[0], per IEEE 754 double precision) -- while
// staying far tighter than any real, distinct ckmeans cluster centroids
// (e.g. this file's own tests use values 1000 vs 1001, whose signed-log gap
// is ~4e-4, over 30x looser than this threshold even at that magnitude).
const EFFECTIVELY_SINGLE_VALUE_RELATIVE_TOLERANCE = 1e-6;

export const buildGradientLegend = (scale: LogCkmeansColorScale, formatTick: (value: number) => string | 0): GradientLegend => {
    const { logBreaks, breaks, colors } = scale;

    // colorForLogCkmeansValue/the Mapbox expression clamp every real value to
    // logBreaks[0]'s color once logBreaks[last] and logBreaks[0] are this
    // close, so the hexagons actually painted are a single flat color, not a
    // gradient (see the padding this detects, above). Render a legend that
    // matches what's actually on the map: one color, one tick, instead of a
    // two-color gradient whose end labels round to the same displayed number.
    const isEffectivelySingleValue = logBreaks[logBreaks.length - 1] - logBreaks[0] <= Math.max(Math.abs(logBreaks[0]) * EFFECTIVELY_SINGLE_VALUE_RELATIVE_TOLERANCE, Number.EPSILON);

    if (isEffectivelySingleValue) {
        return {
            gradientBackground: colors[0],
            ticks: [{ left: '50%', label: `${formatTick(breaks[0])}` }],
        };
    }

    const positions = colors.map((_, index) => (colors.length === 1 ? 0 : (index / (colors.length - 1)) * 100));

    return {
        gradientBackground: `linear-gradient(to right, ${colors.map((color, index) => `${color} ${positions[index]}%`).join(', ')})`,
        ticks: breaks.map((value, index) => ({
            left: `${positions[index]}%`,
            label: `${formatTick(value)}`,
        })),
    };
};

export const getHexagonsFeatureCollection = (data: HexagonData, zoom) => {
    if (data.empty) return null;

    const hexIdxGrp = data.toHexagonDataMap(zoom);

    const hexagons = geojson2h3.h3SetToFeatureCollection([...hexIdxGrp.keys()], (hex) => ({
        value: hexIdxGrp.get(hex),
        // Mirrors of value.data/value.length as top-level, flat properties:
        // Mapbox GL's `interpolate`/`filter` expressions cannot reliably
        // read a property nested inside another property once it has gone
        // through the geojson-vt tiling Mapbox uses for `type: 'geojson'`
        // sources, so paint/filter expressions read these flat copies while
        // click/hover handlers keep reading the nested `value` object.
        dataValue: hexIdxGrp.get(hex)?.data ?? 0,
        itemLength: hexIdxGrp.get(hex)?.length ?? 0,
        size: zoomScale(zoom),
    }));

    preserveOriginalCoordinates(hexagons);
    fixTransmeridian(hexagons);
    densifyHexagons(hexagons);
    return hexagons;
};

function preserveOriginalCoordinates(featureCollection: any) {
    featureCollection.features.forEach((feature: any) => {
        feature.properties.value.originalCoordinates = cloneCoordinates(feature.geometry.coordinates);
    });
}

function cloneCoordinates(coordinates: any) {
    return JSON.parse(JSON.stringify(coordinates));
}

export function densifyHexagons(featureCollection: any, maxSegmentLengthDegrees = 2, polarSegmentLengthDegrees = 0.25, polarLatitudeThreshold = 85) {
    if (!featureCollection?.features) {
        return featureCollection;
    }

    featureCollection.features.forEach((feature: any) => {
        const segmentLength = isPolarFeature(feature, polarLatitudeThreshold) ? polarSegmentLengthDegrees : maxSegmentLengthDegrees;

        if (feature.geometry?.type === 'Polygon') {
            feature.geometry.coordinates = feature.geometry.coordinates.map((ring: number[][]) => densifyRing(ring, segmentLength));
        }

        if (feature.geometry?.type === 'MultiPolygon') {
            feature.geometry.coordinates = feature.geometry.coordinates.map((polygon: number[][][]) =>
                polygon.map((ring: number[][]) => densifyRing(ring, segmentLength))
            );
        }
    });

    return featureCollection;
}

function densifyRing(ring: number[][], maxSegmentLengthDegrees: number) {
    if (!ring?.length) {
        return ring;
    }

    const densified: number[][] = [];
    for (let i = 0; i < ring.length - 1; i++) {
        const start = ring[i];
        const end = ring[i + 1];
        densified.push(start);

        const angularDistanceDegrees = greatCircleAngleDegrees(start, end);
        // Subtract a tiny epsilon before rounding up: trig round-trips (acos of a
        // dot product) can push an angle that is exactly a multiple of the
        // threshold fractionally above it, adding an unwanted extra step.
        const steps = Math.ceil(angularDistanceDegrees / maxSegmentLengthDegrees - 1e-9);

        for (let step = 1; step < steps; step++) {
            densified.push(slerp(start, end, step / steps));
        }
    }

    densified.push(ring[ring.length - 1]);
    return densified;
}

// Interpolates along the great-circle arc between two [lng, lat] points instead
// of lerping lng/lat directly. Near the poles, meridians converge, so a tiny
// physical/angular distance can correspond to a huge raw longitude delta.
// Lng/lat-linear interpolation both over-subdivided edges near the poles and
// bulged the interpolated path outward into a circle instead of following the
// true (near-straight) edge.
function slerp(start: number[], end: number[], t: number): number[] {
    const startVector = toUnitVector(start);
    const endVector = toUnitVector(end);
    const theta = Math.acos(clampToUnitRange(dotProduct(startVector, endVector)));

    if (theta < 1e-12) {
        return start;
    }

    const sinTheta = Math.sin(theta);
    const startWeight = Math.sin((1 - t) * theta) / sinTheta;
    const endWeight = Math.sin(t * theta) / sinTheta;
    const interpolatedVector = [
        startWeight * startVector[0] + endWeight * endVector[0],
        startWeight * startVector[1] + endWeight * endVector[1],
        startWeight * startVector[2] + endWeight * endVector[2],
    ];

    return matchLongitudeContinuity(fromUnitVector(interpolatedVector), start[0]);
}

function greatCircleAngleDegrees(start: number[], end: number[]): number {
    const dot = clampToUnitRange(dotProduct(toUnitVector(start), toUnitVector(end)));
    return (Math.acos(dot) * 180) / Math.PI;
}

function toUnitVector([longitude, latitude]: number[]): number[] {
    const lngRad = (longitude * Math.PI) / 180;
    const latRad = (latitude * Math.PI) / 180;
    return [Math.cos(latRad) * Math.cos(lngRad), Math.cos(latRad) * Math.sin(lngRad), Math.sin(latRad)];
}

function fromUnitVector([x, y, z]: number[]): number[] {
    const latitude = (Math.asin(clampToUnitRange(z)) * 180) / Math.PI;
    const longitude = (Math.atan2(y, x) * 180) / Math.PI;
    return [longitude, latitude];
}

function dotProduct(a: number[], b: number[]): number {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function clampToUnitRange(value: number): number {
    return Math.max(-1, Math.min(1, value));
}

// atan2 returns longitude in (-180, 180]; re-wrap so the interpolated point
// stays continuous with the ring's existing longitude representation (which
// may already be shifted outside that range by fixTransmeridian).
function matchLongitudeContinuity(point: number[], referenceLongitude: number): number[] {
    let [longitude, latitude] = point;

    while (longitude - referenceLongitude > 180) {
        longitude -= 360;
    }

    while (longitude - referenceLongitude < -180) {
        longitude += 360;
    }

    return [longitude, latitude];
}

function isPolarFeature(feature: any, polarLatitudeThreshold: number) {
    return getFeatureMaxAbsLatitude(feature) >= polarLatitudeThreshold;
}

// Mapbox GL JS tiles `type: 'geojson'` sources internally via geojson-vt, which
// projects through Web Mercator regardless of the map's display projection.
// That silently distorts/clips polygons above ~85 degrees latitude even under
// `globe` projection. Cells at or beyond the threshold are rendered instead by
// PolarHexagonCanvasOverlay, which projects vertices with `map.project()` and
// bypasses geojson-vt entirely.
export function partitionPolarFeatures(featureCollection: any, latThreshold = 85) {
    const nonPolar: any = { type: 'FeatureCollection', features: [] };
    const polar: any = { type: 'FeatureCollection', features: [] };

    if (!featureCollection?.features) {
        return { nonPolar, polar };
    }

    featureCollection.features.forEach((feature: any) => {
        const target = getFeatureMaxAbsLatitude(feature) >= latThreshold ? polar : nonPolar;
        target.features.push(cloneFeature(feature));
    });

    return { nonPolar, polar };
}

function getFeatureMaxAbsLatitude(feature: any) {
    const coordinates = feature.geometry?.coordinates || [];
    const latitudes = flattenCoordinates(coordinates)
        .map((coordinate: number[]) => coordinate[1])
        .filter((latitude: number) => Number.isFinite(latitude));

    if (!latitudes.length) {
        return 0;
    }

    return Math.max(...latitudes.map((latitude: number) => Math.abs(latitude)));
}

function flattenCoordinates(coordinates: any): number[][] {
    if (!Array.isArray(coordinates)) {
        return [];
    }

    if (typeof coordinates[0] === 'number') {
        return [coordinates as number[]];
    }

    return coordinates.reduce((result: number[][], coordinate: any) => result.concat(flattenCoordinates(coordinate)), []);
}

function cloneFeature(feature: any) {
    return JSON.parse(JSON.stringify(feature));
}

function calculatePopulationStdev(data) {
    if (data.length <= 1) {
        return 0;
    }
    const mean = d3.mean(data);
    const squaredDifferences = data.map((val) => Math.pow(val - mean, 2));
    const variance = d3.sum(squaredDifferences) / data.length;
    const populationStdev = Math.sqrt(variance);

    return populationStdev;
}

export function fixTransmeridian(feature) {
    const { type } = feature;
    if (type === 'FeatureCollection') {
        feature.features.map(fixTransmeridian);
        return;
    }
    const { type: geometryType, coordinates } = feature.geometry;
    switch (geometryType) {
        case 'LineString':
            fixTransmeridianLoop(coordinates);
            return;
        case 'Polygon':
            fixTransmeridianPolygon(coordinates);
            return;
        case 'MultiPolygon':
            coordinates.forEach(fixTransmeridianPolygon);
            return;
        default:
            throw new Error(`Unknown geometry type: ${geometryType}`);
    }
}

function fixTransmeridianCoord(coord) {
    const lng = coord[0];
    coord[0] = lng < 0 ? lng + 360 : lng;
}

function fixTransmeridianLoop(loop) {
    let isTransmeridian = false;
    for (let i = 0; i < loop.length; i++) {
        // check for arcs > 180 degrees longitude, flagging as transmeridian
        if (Math.abs(loop[0][0] - loop[(i + 1) % loop.length][0]) > 180) {
            isTransmeridian = true;
            break;
        }
    }
    if (isTransmeridian) {
        loop.forEach(fixTransmeridianCoord);
    }
}

function fixTransmeridianPolygon(polygon) {
    polygon.forEach(fixTransmeridianLoop);
}

function gmean(data, value = (d) => d) {
    const r = 64;
    const K = 2 ** r;
    const K1 = 2 ** -r;
    let p = 1; // product
    let n = 0; // count
    let s = 1; // sign
    let k = 0; // track exponent to prevent under/overflows
    for (let i = 0; i < data.length; i++) {
        const v = value(data[i]);
        if (+v === +v) {
            n++;
            s = Math.sign(v);
            if (s === 0) return 0;
            p *= Math.abs(v);
            while (p > K) (p *= K1), ++k;
            while (p < K1) (p *= K), --k;
        }
    }
    return n ? s * 2 ** ((Math.log2(p) + k * r) / n) : NaN;
}

function gstddev(data: Array<number>) {
    // implementation of EXP(STDEV.P(LN(data)))
    return Math.exp(calculatePopulationStdev(data.map((d) => Math.log(d))));
}

export const findMaxCoordinateByFeature = (features: any): [number, number] => {
    let maxData = -Infinity;
    let maxCoordinate: [number, number] | undefined;

    features.forEach((feature) => {
        const { data } = feature.properties.value;
        if (data > maxData) {
            maxData = data;
            const [longitude, latitude] = feature.geometry.coordinates[0][0];
            maxCoordinate = [longitude, latitude];
        }
    });

    return maxCoordinate || [0, 0];
};

export const findMaxCoordinateByData = (data: any): [number, number] => {
    let maxData = -Infinity;
    let maxCoordinate: [number, number] | undefined;

    data.forEach((measurement: MeasurementDataDTO) => {
        let data = measurement.activity ?? 0;
        if (data > maxData) {
            maxData = data;
            const [longitude, latitude] = [measurement.longitude, measurement.latitude];
            maxCoordinate = [longitude, latitude];
        }
    });

    return maxCoordinate || [0, 0];
};

export const parse_measurements_minimized = (s: string) => {
    if (!s.trim()) return [];

    const data = s.split('|').map((item) => {
        const [
            activity,
            activityCorr,
            begPeriod,
            detection,
            endPeriod,
            latitude,
            longitude,
            nuSymbol,
            profileId,
            refId,
            sampleId,
            sampleType,
            samplingDepth,
            unit,
            uncertanityCorr,
            unitCorr,
            nuclideId,
        ] = item.split(';');

        return {
            activity: Number(activity),
            activityCorr: activityCorr && Number(activityCorr),
            begPeriod: begPeriod && new Date(begPeriod),
            detection: detection,
            endPeriod: endPeriod && new Date(endPeriod),
            latitude: Number(latitude),
            longitude: Number(longitude),
            nuSymbol: nuSymbol,
            nuclideId: nuclideId,
            profileId: profileId,
            refId: Number(refId),
            sampleId: Number(sampleId),
            sampleType: sampleType,
            samplingDepth: Number(samplingDepth),
            unit: unit,
            uncertanityCorr: Number(uncertanityCorr),
            unitCorr: unitCorr,
        };
    });

    return data;
};

const specifierScale = d3.scaleThreshold([-2, -1, 0, 1, 3, 4, 5, 6], ['.2e', '.3', '.3', '.3', '.4', ',.4', ',.5', ',.6', '.2e']);

export const d3smartFormat = (value) => {
    if (value === undefined) return '';
    if (value === 0) return 0;

    const isNeg = value < 0;
    const absValue = Math.abs(value);
    const powerOf10 = Math.log10(absValue);
    const formattedValue = d3.format(specifierScale(powerOf10 as any))(absValue);

    return isNeg ? `-${formattedValue}` : formattedValue;
};

export const formatWithoutTrailingZeros = (value) => {
    // check the value and find the number of preluding zeros
    let zeros = 0;
    let temp = value;
    while (temp > 0 && temp < 1) {
        temp *= 10;
        zeros++;
    }
    // format the value and remove the trailing zeros
    return value?.toFixed(zeros + 1).replace(/\.0+$/, '');
};

export const formatSelectingOnlyFirstTwoDecimalDigits = (n) => {
    // if the number doesn't have decimal digits, return it
    if (n % 1 === 0) {
        return n;
    }

    // if the number has decimal digits, return the number including only first two decimal digits that are not zero
    // example: 1.000000000000000120034 -> 1.00000000000000012
    // example: 1.0003412 -> 1.00034
    // example: 1.00003401 -> 1.000034

    let result = n.toString();
    let decimalDigits = result.split('.')[1];
    let decimalDigitsWithoutTrailingZeros = decimalDigits.replace(/0+$/, '');
    let decimalDigitsWithoutTrailingZerosAndLeadingZero = decimalDigitsWithoutTrailingZeros.replace(/^0+/, '');
    let decimalDigitsWithoutTrailingZerosAndLeadingZeroWithTwoDecimalDigits = decimalDigitsWithoutTrailingZerosAndLeadingZero.substring(0, 2);
    result = result.split('.')[0] + '.' + decimalDigitsWithoutTrailingZerosAndLeadingZeroWithTwoDecimalDigits;

    return result;
};

// Used for the (HTML, not SVG) gradient legend title via [innerHTML], so
// superscripts use the native <sup> element rather than SVG <tspan>/dy
// offsets (which have no meaning outside an SVG <text> element).
export const formatStringWithUnitToHtml = (string) => {
    const replacements = {
        'Bq/m3)': 'Bq/m<sup>3</sup>)',
        'Bq/m2)': 'Bq/m<sup>2</sup>)',
        'Bq/kgd': 'Bq/kg d.w.',
        'Bq/kgw': 'Bq/kg f.w.',
        'DELTA/mill)': 'Δ<sup>14</sup>C‰)',
        'atom/kgd': 'atom/kg d.w.',
        'atom/kgw': 'atom/kg f.w.',
    };

    Object.keys(replacements).forEach((unit) => {
        string = string.replace(unit, replacements[unit]);
    });

    return string;
};
